import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router';
import {
  Box, Card, Button, Chip, Typography,
  Divider, CircularProgress,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import { useDialog } from 'common/DialogProvider';
import { HRApiWrapper } from 'api/HRApiWrapper';
import ProfileDrawer from './ProfileDrawer';
import ProfileDetail from './ProfileDetail';
import WorkExperienceSection from './WorkExperienceSection';
import EmployeeContractsSection from './EmployeeContractsSection';

interface EmployeeOption {
  id: number;
  name: string;
  lastName: string;
}

const ProfilesPage: React.FC = () => {
  const api: Api = useApi();
  const { employeeApi } = api;
  const hrApi = new HRApiWrapper(employeeApi);
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();
  const location = useLocation();
  const navState = location.state as { employeeId?: number; employeeName?: string; employeeLastName?: string } | null;

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(
    navState?.employeeId ? { id: navState.employeeId, name: navState.employeeName ?? '', lastName: navState.employeeLastName ?? '' } : null
  );
  const [employeeInputValue, setEmployeeInputValue] = useState(
    navState?.employeeId ? `${navState.employeeName ?? ''} ${navState.employeeLastName ?? ''}`.trim() : ''
  );
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  const [profiles, setProfiles] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [seniorities, setSeniorities] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [skillLevels, setSkillLevels] = useState<any[]>([]);
  const [trainingCourses, setTrainingCourses] = useState<any[]>([]);
  const [certifications, setCertifications] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [languageLevels, setLanguageLevels] = useState<any[]>([]);
  const [corporateEntities, setCorporateEntities] = useState<any[]>([]);
  const [contractLookups, setContractLookups] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [errors] = useState({});
  const [paginationModel, setPaginationModel] = usePaginationPreference('hrProfiles', 25);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchEmployees = useCallback((term: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setEmployeeLoading(true);
      try {
        const res = await employeeApi.apiVversionEmployeeSearchGet('1', term, 1, 20);
        const results = (res.data?.data?.queryResult ?? []) as any[];
        setEmployeeOptions(results.filter((e) => e.id != null) as EmployeeOption[]);
      } catch (e) {
        handleAPIError(e, toast, errors);
      } finally {
        setEmployeeLoading(false);
      }
    }, 300);
  }, [employeeApi]);

  const fetchLookups = useCallback(async () => {
    setLoadingLookups(true);
    try {
      const [rolesRes, senRes, skillsRes, levelsRes, coursesRes, certsRes, lookupsRes, langsRes, langLevelsRes, entitiesRes] = await Promise.all([
        api.projectApi.apiVversionListRolesGet('1'),
        hrApi.getSeniorities(),
        hrApi.getSkills('1', 1, 1000),
        hrApi.getSkillLevels(),
        hrApi.getTrainingCourses('1', 1, 1000),
        hrApi.getTrainingCertifications('1', 1, 1000),
        hrApi.getContractLookups(),
        hrApi.getLanguages(),
        hrApi.getLanguageLevels(),
        api.invoiceApi.apiVversionCorporateEntitySearchGet('1', '', 1, 100),
      ]);
      setRoles(rolesRes?.data?.data?.queryResult ?? []);
      setSeniorities(senRes.data?.data ?? senRes.data ?? []);
      setSkills(skillsRes.data?.data?.queryResult ?? skillsRes.data?.data ?? []);
      setSkillLevels(levelsRes.data?.data ?? levelsRes.data ?? []);
      setTrainingCourses(coursesRes.data?.data?.queryResult ?? coursesRes.data?.data ?? []);
      setCertifications(certsRes.data?.data?.queryResult ?? certsRes.data?.data ?? []);
      setContractLookups(lookupsRes.data?.data ?? lookupsRes.data);
      setLanguages(langsRes.data?.data ?? langsRes.data ?? []);
      setLanguageLevels(langLevelsRes.data?.data ?? langLevelsRes.data ?? []);
      setCorporateEntities(entitiesRes.data?.data?.queryResult ?? []);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoadingLookups(false);
    }
  }, []);

  const fetchProfiles = useCallback(async (employeeId: number) => {
    setLoading(true);
    try {
      const res = await hrApi.getProfilesByEmployee('1', employeeId);
      setProfiles(res.data?.data ?? []);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLookups(); }, []);

  useEffect(() => {
    if (selectedEmployee) {
      fetchProfiles(selectedEmployee.id);
    } else {
      setProfiles([]);
    }
  }, [selectedEmployee]);

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try {
            await hrApi.deleteProfile('1', id);
            fetchProfiles(selectedEmployee!.id);
          } catch (error) { handleAPIError(error, toast, errors); }
          finally { closeDialog(dialogId); }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'role', headerName: intl.get('hr.profile.role'), flex: 1, valueGetter: (_: any, row: any) => row.role?.name ?? '' },
    { field: 'seniority', headerName: intl.get('hr.profile.seniority'), width: 160, valueGetter: (_: any, row: any) => row.seniority?.name ?? '' },
    {
      field: 'skillCount', headerName: intl.get('hr.profile.skills'), width: 120,
      renderCell: (params) => (
        <Chip label={(params.row.profileSkills ?? []).filter((s: any) => !s.isDeleted).length} size="small" />
      ),
    },
    {
      field: 'cvFileUri', headerName: intl.get('hr.profile.cv'), width: 100,
      renderCell: (params) => params.value
        ? <Chip label="CV" size="small" color="success" />
        : <Chip label={intl.get('hr.profile.noCV')} size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 140, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={() => { setSelected(params.row); setDetailOpen(true); }}
          onDelete={() => handleDelete(params.row.id)}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 3 }}>
          {intl.get('mainNavigation.humanResources.profiles')}
        </Typography>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Autocomplete
            sx={{ minWidth: 320 }}
            size="small"
            options={employeeOptions}
            getOptionLabel={(o) => `${o.name} ${o.lastName}`}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            loading={employeeLoading}
            value={selectedEmployee}
            inputValue={employeeInputValue}
            onInputChange={(_e, value, reason) => {
              setEmployeeInputValue(value);
              if (reason === 'input') searchEmployees(value);
              if (reason === 'clear') {
                setSelectedEmployee(null);
                setEmployeeOptions([]);
              }
            }}
            onChange={(_e, value) => setSelectedEmployee(value)}
            filterOptions={(x) => x}
            noOptionsText={employeeInputValue.length < 1 ? intl.get('hr.profile.selectEmployee') : intl.get('common.noResults') || 'No results'}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.get('hr.profile.selectEmployee')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {employeeLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selectedEmployee && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDrawerOpen(true); }}>
              {intl.get('hr.profile.add')}
            </Button>
          )}
        </Box>

        {loading ? <Spinner /> : selectedEmployee ? (
          <Box style={{ width: '100%', display: 'flex' }}>
            <DataGrid
              rows={profiles}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              sortingMode="client"
              disableRowSelectionOnClick
            />
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">{intl.get('hr.profile.selectEmployeePrompt')}</Typography>
          </Box>
        )}

        {selectedEmployee && (
          <>
            <Divider sx={{ my: 4 }} />
            <WorkExperienceSection
              employeeId={selectedEmployee.id}
              hrApi={hrApi}
            />
            <Divider sx={{ my: 4 }} />
            <EmployeeContractsSection
              employeeId={selectedEmployee.id}
              hrApi={hrApi}
              contractLookups={contractLookups}
            />
          </>
        )}
      </Box>

      <ProfileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={async (data) => {
          await hrApi.createProfile('1', { ...data, idEmployee: selectedEmployee!.id });
          setDrawerOpen(false);
          fetchProfiles(selectedEmployee!.id);
        }}
        item={null}
        roles={roles}
        seniorities={seniorities}
      />

      {selected && (
        <ProfileDetail
          open={detailOpen}
          onClose={() => { setDetailOpen(false); fetchProfiles(selectedEmployee!.id); }}
          profile={selected}
          hrApi={hrApi}
          skills={skills}
          skillLevels={skillLevels}
          trainingCourses={trainingCourses}
          certifications={certifications}
          roles={roles}
          seniorities={seniorities}
          languages={languages}
          languageLevels={languageLevels}
          corporateEntities={corporateEntities}
        />
      )}
    </Card>
  );
};

export default ProfilesPage;
